/**
 * Module Ventes, lot 1 : carnet de compagnies, contacts et journal
 * d'activités. Réservé aux admins et au superadmin (migration 016, RLS).
 * En ligne seulement (pas de copie hors ligne) : c'est un outil de bureau.
 */
import { supabase } from './supabase'

export type Categorie = 'entrepreneur' | 'client' | 'fournisseur' | 'professionnel' | 'autre'
export type TypeActivite = 'telephone' | 'courriel' | 'rencontre' | 'suivi' | 'visite' | 'note'

export interface Compagnie {
  id: string
  nom: string
  categorie: Categorie | null
  telephone: string | null
  courriel: string | null
  adresse: string | null
  site_web: string | null
  notes: string | null
  cree_le: string
  modifie_le: string
}

export interface ContactCrm {
  id: string
  compagnie_id: string | null
  nom: string
  fonction: string | null
  telephone: string | null
  cellulaire: string | null
  courriel: string | null
  notes: string | null
  cree_le: string
}

export interface Activite {
  id: string
  compagnie_id: string | null
  contact_id: string | null
  quand: string
  type: TypeActivite
  objet: string
  notes: string | null
  relance_le: string | null
  relance_faite: boolean
  par: string | null
  par_nom: string | null
  cree_le: string
  auteur?: { nom: string } | null
}

export const CATEGORIES: { cle: Categorie; libelle: string }[] = [
  { cle: 'entrepreneur', libelle: 'Entrepreneur général' },
  { cle: 'client', libelle: 'Client direct' },
  { cle: 'professionnel', libelle: 'Professionnel (archi., ingé.)' },
  { cle: 'fournisseur', libelle: 'Fournisseur' },
  { cle: 'autre', libelle: 'Autre' }
]

export const TYPES_ACTIVITE: { cle: TypeActivite; libelle: string }[] = [
  { cle: 'telephone', libelle: 'Téléphone' },
  { cle: 'courriel', libelle: 'Courriel' },
  { cle: 'rencontre', libelle: 'Rencontre' },
  { cle: 'visite', libelle: 'Visite de chantier' },
  { cle: 'suivi', libelle: 'Suivi' },
  { cle: 'note', libelle: 'Note' }
]

export const libelleCategorie = (c: Categorie | null) => CATEGORIES.find((x) => x.cle === c)?.libelle ?? null
export const libelleType = (t: TypeActivite) => TYPES_ACTIVITE.find((x) => x.cle === t)?.libelle ?? t

/** Sans accents, minuscules, espaces réduits : pour la recherche et les doublons. */
export function normaliser(s: string | null | undefined): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/** Nom de compagnie comparable : sans « inc. », « ltée », ponctuation. */
export function cleCompagnie(s: string | null | undefined): string {
  return normaliser(s)
    .replace(/\b(inc|ltee|ltd|limitee|enr|senc|cie)\b\.?/g, '')
    .replace(/[^a-z0-9]/g, '')
}

/** Tous les termes de la requête doivent se trouver dans le texte. */
export function correspond(requete: string, ...champs: (string | null | undefined)[]): boolean {
  const termes = normaliser(requete).split(' ').filter(Boolean)
  if (termes.length === 0) return true
  const texte = normaliser(champs.filter(Boolean).join(' '))
  const chiffres = texte.replace(/\D/g, '')
  return termes.every((t) => texte.includes(t) || (/^\d{3,}$/.test(t) && chiffres.includes(t)))
}

export const vide = (v: string) => v.trim() || null

export const aujourdhui = () => {
  const d = new Date()
  const n = (x: number) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${n(d.getMonth() + 1)}-${n(d.getDate())}`
}

export interface Carnet {
  compagnies: Compagnie[]
  contacts: ContactCrm[]
  activites: Activite[]
}

/** Lit tout le carnet (quelques centaines de lignes : on filtre à l'écran). */
export async function lireCarnet(): Promise<Carnet> {
  const tout = async <T,>(table: string, select: string, ordre: string, asc = true): Promise<T[]> => {
    const lignes: T[] = []
    for (let de = 0; ; de += 1000) {
      const { data, error } = await supabase
        .from(table)
        .select(select)
        .is('supprime_le', null)
        .order(ordre, { ascending: asc })
        .range(de, de + 999)
      if (error) throw new Error(error.message)
      lignes.push(...((data as T[]) ?? []))
      if (!data || data.length < 1000) break
    }
    return lignes
  }
  const [compagnies, contacts, activites] = await Promise.all([
    tout<Compagnie>('compagnies', '*', 'nom'),
    tout<ContactCrm>('contacts', '*', 'nom'),
    tout<Activite>('activites', '*, auteur:profils!activites_par_fkey(nom)', 'quand', false)
  ])
  const tri = (a: { nom: string }, b: { nom: string }) => a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' })
  return { compagnies: compagnies.sort(tri), contacts: contacts.sort(tri), activites }
}

/** Lien tel: utilisable (chiffres et +). */
export const lienTel = (t: string) => `tel:${t.replace(/[^\d+]/g, '')}`
